import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Factory, ShieldCheck, Ruler, Clock } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import Layout from "../Layout";
import SEOHead from "../SEOHead";
import Breadcrumb from "../Breadcrumb";
import FAQSection from "../FAQSection";
import { industries } from "../../data/industries";
import { business } from "../../config/business";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const approach = [
  { icon: Ruler, title: "Requirement Study", text: "We start with your industry's specific structural, hygiene and operational needs." },
  { icon: ShieldCheck, title: "Load & Compliance Design", text: "Structures engineered to the loads and standards your industry actually requires." },
  { icon: Factory, title: "Precision Fabrication", text: "Steel fabricated to the exact specification your industry demands." },
  { icon: Clock, title: "Delivery & Erection", text: "On-site erection managed through to a handover-ready facility." },
];

const faqs = [
  { q: "Do you only work with the industries listed here?", a: "These are the industries we have the most structural design experience with, but we take on projects for a wide range of industrial requirements — get in touch if yours isn't listed." },
  { q: "Does industry type significantly change project cost?", a: "Yes, often — hygiene-grade finishes, crane systems, insulation and specialized flooring all add cost relative to a basic shed, so specification should be scoped early." },
];

const IndustriesHub = () => {
  return (
    <Layout>
      <SEOHead
        title="Industries We Serve | PEB & Industrial Structures — TR Structures"
        description="TR Structures designs PEB buildings and industrial sheds for manufacturing, warehousing, pharmaceutical, food processing, automobile, agriculture and more."
        keywords="PEB industries served, industrial shed by industry, PEB manufacturer for pharma, PEB for food processing"
      />
      <Breadcrumb items={[{ name: "Industries" }]} />

      {/* 1. Hero */}
      <section className="section-padding pb-8">
        <div className="container-custom">
          <h1 className="font-montserrat text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6 max-w-3xl">
            Industries We <span className="text-secondary">Build For</span>
          </h1>
          <p className="text-muted-foreground max-w-3xl text-lg mb-8">
            Every industry has different structural priorities — floor loads, ventilation, hygiene,
            crane requirements. We design each PEB structure around the industry it actually serves.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/enquiry" className="btn-primary inline-block px-8 py-4 text-center">
              Get Project Estimate
            </Link>
            <a href={business.whatsapp.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-8 py-4 rounded-lg font-montserrat font-semibold hover:bg-[#20bd5a] transition">
              <FaWhatsapp size={20} aria-hidden="true" /> WhatsApp Us
            </a>
          </div>
        </div>
      </section>

      {/* 2. Trust strip */}
      <section className="bg-primary py-6">
        <div className="container-custom">
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-primary-foreground text-center">
            <span className="font-montserrat font-semibold flex items-center gap-2"><ShieldCheck size={18} aria-hidden="true" /> Industry-specific design</span>
            <span className="font-montserrat font-semibold flex items-center gap-2"><Clock size={18} aria-hidden="true" /> Fast fabrication & erection</span>
            <span className="font-montserrat font-semibold flex items-center gap-2"><Factory size={18} aria-hidden="true" /> 12+ industry verticals served</span>
          </div>
        </div>
      </section>

      {/* 3. Industries grid */}
      <section className="section-padding pt-14">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            transition={{ staggerChildren: 0.06 }}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {industries.map((industry) => (
              <motion.div key={industry.slug} variants={fadeUp}>
                <Link
                  to={`/industries/${industry.slug}`}
                  className="p-6 bg-card rounded-xl shadow-md card-hover border border-border block h-full"
                >
                  <div className="w-11 h-11 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <Factory className="text-primary" size={20} aria-hidden="true" />
                  </div>
                  <h2 className="font-montserrat font-bold text-foreground mb-2">{industry.name}</h2>
                  <p className="text-sm text-muted-foreground">{industry.tagline}</p>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 4. Our approach */}
      <section className="section-padding bg-muted">
        <div className="container-custom">
          <h2 className="font-montserrat text-2xl md:text-3xl font-bold text-foreground mb-10 text-center">
            How We Approach Every Industry
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {approach.map((step, i) => (
              <div key={step.title} className="p-6 bg-card rounded-xl shadow-md text-center card-hover">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <step.icon className="text-primary" size={22} aria-hidden="true" />
                </div>
                <p className="text-xs font-montserrat font-bold text-secondary mb-2">STEP {i + 1}</p>
                <h3 className="font-montserrat font-bold text-foreground mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Related services */}
      <section className="section-padding">
        <div className="container-custom">
          <h2 className="font-montserrat text-2xl font-bold text-foreground mb-6 text-center">
            Related Services
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { name: "PEB Buildings", path: "/pre-fab-structure-buildings" },
              { name: "Industrial Sheds", path: "/industrial-sheds" },
              { name: "Steel Fabrication", path: "/structure-steel-fabrication" },
              { name: "Design & Engineering", path: "/design-engineering" },
            ].map((service) => (
              <Link
                key={service.path}
                to={service.path}
                className="p-4 bg-muted rounded-lg text-center font-montserrat font-semibold text-foreground hover:bg-primary hover:text-primary-foreground transition"
              >
                {service.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 6. FAQ */}
      <FAQSection faqs={faqs} />

      {/* 7. CTA */}
      <section className="section-padding bg-secondary">
        <div className="container-custom text-center">
          <h2 className="font-montserrat text-2xl md:text-3xl font-bold text-secondary-foreground mb-4">
            Don't See Your Industry Listed?
          </h2>
          <p className="text-secondary-foreground/90 mb-8 max-w-xl mx-auto">
            We design PEB structures for a wide range of industrial requirements — tell us about your
            project and we'll work through the specifics with you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/enquiry" className="btn-primary inline-block px-8 py-4">
              Discuss Your Project
            </Link>
            <a href={business.whatsapp.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-8 py-4 rounded-lg font-montserrat font-semibold hover:bg-[#20bd5a] transition">
              <FaWhatsapp size={20} aria-hidden="true" /> WhatsApp Us
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default IndustriesHub;
